import React, { useState } from 'react';
import {
  Box, Button, Collapse, Flex, IconButton, Input, Link, Text, useToast,
} from '@chakra-ui/react';
import {
  FiExternalLink, FiLink, FiPlus, FiTrash2,
} from 'react-icons/fi';
import GlassCard from './GlassCard';
import AuroraBackground from './dashboard/AuroraBackground';
import PageHeader from './PageHeader';
import useProjectLinks, { useProjectLinkActions } from '../hooks/useProjectLinks';
import apiErrorMessage from '../utils/apiError';

// item 329 — the project LINKS section: named URLs (documentation, configuration
// software, vendor portals) pinned to the project, next to the uploaded documents.
// Links are their own thing, not files — no upload lifecycle, just name + URL.
const ProjectLinksCard: React.FC<{
  projectId: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
}> = ({ projectId, collapse }) => {
  const { data: links } = useProjectLinks(projectId);
  const { add, remove } = useProjectLinkActions(projectId);
  const toast = useToast();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');

  const onAdd = async () => {
    if (!name.trim() || !url.trim()) return;
    try {
      await add.mutateAsync({ name: name.trim(), url: url.trim() });
      setName('');
      setUrl('');
    } catch (e) {
      toast({
        title: 'Could not add the link',
        description: apiErrorMessage(e, 'Check the URL and try again.'),
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    }
  };

  const onRemove = async (id: string) => {
    try {
      await remove.mutateAsync(id);
    } catch (e) {
      toast({
        title: 'Could not remove the link',
        description: apiErrorMessage(e, 'Try again.'),
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    }
  };

  const collapsed = collapse?.collapsed ?? false;

  return (
    // item 381 — same section language as Documents/Talk: bleed panel + compact header
    <Box
      position="relative"
      mx={{ base: -4, md: -10 }}
      px={{ base: 4, md: 10 }}
      pt={collapsed ? 1 : { base: 2, md: 3 }}
      pb={collapsed ? 1 : { base: 2, md: 3 }}
    >
      <AuroraBackground />
      <Box position="relative" zIndex={1}>
        <PageHeader
          dense
          onTitleClick={collapse?.onToggle}
          collapsed={collapsed}
          icon={<FiLink />}
          title="Links"
          subtitle={`${(links ?? []).length} ${(links ?? []).length === 1 ? 'link' : 'links'}`}
        />
        <Collapse in={!collapsed} animateOpacity>
          <GlassCard p={4}>
            {(links ?? []).length === 0 && (
            <Text fontSize="sm" opacity={0.65} mb={3}>
              No links yet — pin documentation, configuration software, or portals to this project.
            </Text>
            )}
            {(links ?? []).map((l) => (
              <Flex key={l.id} align="center" gap={2} py={1.5} role="group">
                <Link
                  href={l.url}
                  isExternal
                  display="inline-flex"
                  alignItems="center"
                  gap="6px"
                  fontSize="sm"
                  fontWeight="semibold"
                  minW={0}
                >
                  <Text as="span" isTruncated title={l.url}>{l.name}</Text>
                  <FiExternalLink size={13} />
                </Link>
                <Box flex={1} />
                <IconButton
                  aria-label={`Remove link ${l.name}`}
                  icon={<FiTrash2 />}
                  size="xs"
                  variant="ghost"
                  opacity={0.4}
                  _groupHover={{ opacity: 1 }}
                  onClick={() => onRemove(l.id)}
                />
              </Flex>
            ))}
            <Flex mt={3} gap={2} direction={{ base: 'column', md: 'row' }}>
              <Input
                size="sm"
                placeholder="Link name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxW={{ md: '220px' }}
              />
              <Input
                size="sm"
                placeholder="URL (https://…)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') onAdd(); }}
              />
              <Button
                size="sm"
                leftIcon={<FiPlus />}
                isLoading={add.isLoading}
                isDisabled={!name.trim() || !url.trim()}
                onClick={onAdd}
                flexShrink={0}
              >
                Add link
              </Button>
            </Flex>
          </GlassCard>
        </Collapse>
      </Box>
    </Box>
  );
};

ProjectLinksCard.defaultProps = { collapse: undefined };

export default ProjectLinksCard;
